import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ShoppingCart } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { ApiError } from '@/api/client';
import {
  decidePurchaseApproval,
  getPurchaseApproval,
  getPurchaseEnvelopes,
  getPurchaseNotice,
  requestPurchaseApproval,
  sendPurchaseNotice,
  withdrawPurchaseApproval,
} from '@/api/hardwareRequests';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { formatDateTime } from '@/lib/dateFormat';
import type {
  HardwareRequestDetail,
  PurchaseApproval,
  PurchaseApprovalResponse,
  PurchaseEnvelope,
  PurchaseNoticeResponse,
} from '@/types/oscar';
import { envelopeFigures } from './envelope';

/** The column's width. The API refuses longer rather than cutting the note. */
const MAX_NOTE = 400;

type Dialog = 'escalate' | 'approve' | 'decline' | 'withdraw' | 'resend' | null;

/**
 * What an approved request needs BUYING, and the second decision that lets it be bought.
 *
 * <p>Anthony, 28/10/2026: <i>"wouldn't the approve if no stock be an escalation for
 * approval button on the screen"</i>, then <i>"1, per purchase, keep Zendesk
 * internal"</i> (29/10/2026). The asset manager approves the REQUEST as before, so
 * what is in stock can go straight out. What has no stock is escalated, and the IT
 * Finance Approver approves the SPEND.</p>
 *
 * ⛔ INTERNAL. Nothing on this panel is sent to the Zendesk ticket or shown to the
 * requester, and it says so.
 *
 * ⛔ THE BUTTONS ARE THE SERVER'S ANSWER FOR THIS CALLER (CanRequest / CanDecide /
 * CanWithdraw), not the page's guess: the page cannot know who raised the escalation,
 * and a person cannot approve their own.
 *
 * ⛔ THE EMAIL OUTCOME IS SHOWN, NOT ASSUMED. The email to the people who decide goes
 * after the escalation commits and can fail while the escalation stands, so "not
 * sent", and why - including "nobody is on the list" - is said here.
 *
 * ⛔ RENDERS NOTHING for anybody who cannot act on a purchase, and nothing for a
 * request that is not approved or has nothing to buy.
 */
export function PurchaseNeeded({ request }: { request: HardwareRequestDetail }) {
  const { canApproveHardware, canApprovePurchase } = useAuth();
  const queryClient = useQueryClient();
  const submissionId = request.SubmissionId;
  const allowed = canApproveHardware || canApprovePurchase;

  const [dialog, setDialog] = useState<Dialog>(null);
  const [text, setText] = useState('');
  // ⛔ THE ENVELOPE THE APPROVER CHOOSES: '' is no answer yet (and keeps Approve off), 'none' is a
  // deliberate "this draws on no CEA envelope", anything else is the forecast id. Silence is not an
  // answer - a purchase nobody tied to an envelope draws on nothing and warns nobody.
  const [pick, setPick] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const notice = useQuery({
    queryKey: ['hardware-request', submissionId, 'purchase-notice'],
    queryFn: ({ signal }) => getPurchaseNotice(submissionId, signal),
    enabled: allowed,
  });

  const approval = useQuery({
    queryKey: ['hardware-request', submissionId, 'purchase-approval'],
    queryFn: ({ signal }) => getPurchaseApproval(submissionId, signal),
    enabled: allowed,
  });

  // Only the approver picks, and only when they are about to - the list is read when the dialog opens.
  const envelopes = useQuery({
    queryKey: ['purchase-envelopes'],
    queryFn: ({ signal }) => getPurchaseEnvelopes(signal),
    // ⚠ The SERVER'S answer for this caller (CanDecide), not a role guess: it is also false for the person who asked.
    enabled: approval.data?.CanDecide === true && dialog === 'approve',
    retry: false,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['hardware-request', submissionId, 'purchase-notice'] });
    void queryClient.invalidateQueries({ queryKey: ['hardware-request', submissionId, 'purchase-approval'] });
  };

  const close = () => {
    setDialog(null);
    setText('');
    setPick('');
    setError(null);
  };

  const fail = (fallback: string) => (e: unknown) => {
    setError(e instanceof ApiError ? e.message : fallback);
    refresh();
  };

  const escalate = useMutation({
    mutationFn: () => requestPurchaseApproval(submissionId, text.trim() || undefined),
    onSuccess: (r) => {
      close();
      // ⚠ The escalation stands whether or not the email went; say which.
      setInfo(
        r.Notified > 0
          ? `Escalated. ${r.Notified} ${r.Notified === 1 ? 'person was' : 'people were'} emailed.`
          : `Escalated, but nobody was emailed. ${r.NotifyError ?? ''}`.trim(),
      );
      refresh();
    },
    onError: fail('Could not escalate the purchase.'),
  });

  const decide = useMutation({
    mutationFn: (decision: 'Approved' | 'Declined') =>
      decidePurchaseApproval(
        submissionId,
        decision,
        text.trim() || undefined,
        // ⚠ A decline names no envelope; only an approval says where the money comes from.
        decision === 'Approved'
          ? (pick === 'none' ? { NoEnvelope: true as const } : { ProjectForecastId: Number(pick) })
          : undefined,
      ),
    onSuccess: () => {
      close();
      setInfo(null);
      refresh();
    },
    onError: fail('Could not record the decision.'),
  });

  const withdraw = useMutation({
    mutationFn: () => withdrawPurchaseApproval(submissionId),
    onSuccess: () => {
      close();
      setInfo(null);
      refresh();
    },
    onError: fail('Could not withdraw it.'),
  });

  const resend = useMutation({
    mutationFn: () => sendPurchaseNotice(submissionId),
    onSuccess: () => {
      close();
      refresh();
    },
    onError: fail('Could not send the email.'),
  });

  // ⛔ Nothing at all for anybody who cannot act on a purchase.
  if (!allowed) return null;

  const n = notice.data;
  if (!n || !n.Approved || n.Lines.length === 0) return null;

  const a = approval.data;
  const current = a?.Approval ?? null;

  return (
    <section className="mb-6" aria-labelledby="purchase-needed-heading">
      <h2 id="purchase-needed-heading" className="font-heading text-lg font-bold text-navy">
        Needs a purchase
      </h2>
      <p className="mt-1 flex items-start gap-2 text-sm text-darkGrey/80">
        <ShoppingCart className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>
          Nothing is on hand for the following, so it has to be bought. Buying needs the IT Finance Approver.
          This is internal: the requester and the ticket are not told.
        </span>
      </p>

      <ul className="mt-2 space-y-1">
        {n.Lines.map((l) => (
          <li key={`${l.Kind}-${l.Name}`} className="flex flex-wrap items-baseline gap-x-3 text-sm text-darkGrey">
            <span className="font-semibold text-navy">{l.Name}</span>
            <span className="text-xs text-darkGrey/60">{l.Kind}</span>
            <span className="text-xs text-darkGrey/80">{l.Reason}</span>
          </li>
        ))}
      </ul>

      {approval.isError && (
        <p role="alert" className="mt-3 text-sm text-red">
          Could not read where the purchase is. If this keeps happening, contact Onsite IT Support.
        </p>
      )}

      {a && <ApprovalBlock current={current} a={a} />}

      {info && <p className="mt-2 text-sm text-darkGrey">{info}</p>}

      {current?.Status === 'Requested' && <NoticeStatus data={n} />}

      <div className="mt-3 flex flex-wrap gap-2">
        {a?.CanRequest && (
          <Btn primary onClick={() => open('escalate')}>
            {current?.Status === 'Declined' ? 'Escalate again' : 'Escalate for purchase approval'}
          </Btn>
        )}
        {a?.CanDecide && (
          <>
            <Btn primary onClick={() => open('approve')}>
              Approve purchase
            </Btn>
            <Btn onClick={() => open('decline')}>Decline</Btn>
          </>
        )}
        {a?.CanWithdraw && <Btn onClick={() => open('withdraw')}>Withdraw</Btn>}
        {current?.Status === 'Requested' && canApproveHardware && n.RecipientsOnList > 0 && (
          <Btn onClick={() => open('resend')}>Email the approvers again</Btn>
        )}
      </div>

      {error && !dialog && (
        <p role="alert" className="mt-2 rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
          {error}
        </p>
      )}

      <AlertDialog
        open={dialog === 'escalate'}
        onOpenChange={(next) => !next && close()}
        title="Escalate for purchase approval?"
        description={
          <>
            The IT Finance Approver is emailed to approve the spend on what has no stock. Nothing is sent to
            the ticket or the requester.
          </>
        }
        confirmLabel={escalate.isPending ? 'Escalating…' : 'Escalate'}
        confirmDisabled={escalate.isPending}
        closeOnConfirm={false}
        onConfirm={() => escalate.mutate()}
      >
        <NoteBox label="Note for the approver (optional)" value={text} onChange={setText} />
        <Problem message={error} />
      </AlertDialog>

      <AlertDialog
        open={dialog === 'approve'}
        onOpenChange={(next) => !next && close()}
        title="Approve this purchase?"
        description={<>You are approving the spend on: {current?.LinesText}</>}
        confirmLabel={decide.isPending ? 'Approving…' : 'Approve purchase'}
        confirmDisabled={decide.isPending || pick === ''}
        closeOnConfirm={false}
        onConfirm={() => decide.mutate('Approved')}
      >
        <EnvelopePicker pick={pick} onPick={setPick} query={envelopes} />
        <NoteBox label="Note (optional)" value={text} onChange={setText} />
        <Problem message={error} />
      </AlertDialog>

      <AlertDialog
        open={dialog === 'decline'}
        onOpenChange={(next) => !next && close()}
        title="Decline this purchase?"
        description={<>The asset manager is shown your reason. Nothing is sent to the ticket or the requester.</>}
        confirmLabel={decide.isPending ? 'Declining…' : 'Decline'}
        confirmDisabled={text.trim().length === 0 || decide.isPending}
        closeOnConfirm={false}
        onConfirm={() => decide.mutate('Declined')}
      >
        <NoteBox label="Reason (required)" value={text} onChange={setText} />
        <Problem message={error} />
      </AlertDialog>

      <AlertDialog
        open={dialog === 'withdraw'}
        onOpenChange={(next) => !next && close()}
        title="Withdraw the request for approval?"
        description={<>The purchase goes back to needing escalation. A record that it was asked for is kept.</>}
        confirmLabel={withdraw.isPending ? 'Withdrawing…' : 'Withdraw'}
        confirmDisabled={withdraw.isPending}
        closeOnConfirm={false}
        onConfirm={() => withdraw.mutate()}
      >
        <Problem message={error} />
      </AlertDialog>

      <AlertDialog
        open={dialog === 'resend'}
        onOpenChange={(next) => !next && close()}
        title="Email the approvers again?"
        description={
          <>
            {n.Notice?.SentAtUtc
              ? `It has already gone to ${n.Notice.RecipientCount} ${n.Notice.RecipientCount === 1 ? 'person' : 'people'}. This emails the list again.`
              : 'This emails the list now.'}
          </>
        }
        confirmLabel={resend.isPending ? 'Sending…' : 'Send'}
        confirmDisabled={resend.isPending}
        closeOnConfirm={false}
        onConfirm={() => resend.mutate()}
      >
        <Problem message={error} />
      </AlertDialog>
    </section>
  );

  function open(which: Exclude<Dialog, null>) {
    setError(null);
    setText('');
    setDialog(which);
  }
}

function ApprovalBlock({ current, a }: { current: PurchaseApproval | null; a: PurchaseApprovalResponse }) {
  if (!current) {
    return a.WhyNot ? (
      <p className="mt-3 text-sm text-darkGrey/80">{a.WhyNot}</p>
    ) : (
      <p className="mt-3 text-sm text-darkGrey">
        <strong>Not yet escalated.</strong> It cannot be bought until the IT Finance Approver has approved it.
      </p>
    );
  }

  if (current.Status === 'Approved') {
    return (
      <div className="mt-3 text-sm text-darkGrey">
        <p className="flex items-start gap-2">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-navy" aria-hidden />
          <span>
            <strong>Purchase approved</strong> by {current.DecidedBy} on {formatDateTime(current.DecidedAtUtc)}
            {current.DecisionNotes && <> — {current.DecisionNotes}</>}
          </span>
        </p>
        <EnvelopeLine current={current} envelope={a.Envelope} />
      </div>
    );
  }

  if (current.Status === 'Declined') {
    return (
      <p className="mt-3 flex items-start gap-2 text-sm text-darkGrey">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange" aria-hidden />
        <span>
          <strong>Purchase declined</strong> by {current.DecidedBy} on {formatDateTime(current.DecidedAtUtc)} —{' '}
          {current.DecisionNotes}
        </span>
      </p>
    );
  }

  return (
    <div className="mt-3 text-sm text-darkGrey">
      <p>
        <strong>Waiting for purchase approval.</strong> Asked by {current.RequestedBy} on{' '}
        {formatDateTime(current.RequestedAtUtc)}
        {current.RequestNote && <> — {current.RequestNote}</>}
      </p>
      {a.WhyNot && <p className="mt-1 text-xs text-darkGrey/70">{a.WhyNot}</p>}
    </div>
  );
}

/**
 * Which CEA envelope an approved purchase draws on, and where it stands NOW.
 *
 * ⚠ The label is the snapshot taken when it was picked; the figures are read live. If they cannot be
 * read the label still says which envelope - and that the figures are missing, rather than showing a
 * stale copy.
 */
function EnvelopeLine({ current, envelope }: { current: PurchaseApproval; envelope: PurchaseEnvelope | null }) {
  if (current.ProjectForecastId === null) {
    return (
      <p className="mt-1 text-xs text-darkGrey/70">
        Not drawn on a CEA envelope - the approver said it draws on none.
      </p>
    );
  }

  return (
    <p className="mt-1 text-xs text-darkGrey/80">
      {envelope
        ? `Draws on ${envelope.Label}: ${envelopeFigures(envelope)}.`
        : `Draws on ${current.EnvelopeLabel ?? 'a CEA envelope'}. Its figures could not be read just now.`}
    </p>
  );
}

/**
 * The approver says where the money comes from: one CEA envelope, or none.
 *
 * ⛔ NOTHING IS PRE-SELECTED. Approve stays off until a choice is made, because a default would be a
 * decision nobody took. The list shows each envelope's approved / drawn / left, and whether its CEA
 * is approved yet - drawing on one is allowed either way (going over warns, it does not block).
 *
 * ⚠ If the list cannot be read the approver can still say there is none; the sentence says why the
 * list is missing rather than reading as "no envelopes exist".
 */
function EnvelopePicker({
  pick,
  onPick,
  query,
}: {
  pick: string;
  onPick: (v: string) => void;
  query: { data?: PurchaseEnvelope[]; isLoading: boolean; isError: boolean };
}) {
  const options = query.data ?? [];

  return (
    <fieldset className="mb-3">
      <legend className="mb-1 block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
        Which CEA envelope does this draw on?
      </legend>

      {query.isLoading && <p className="text-xs text-darkGrey/70">Loading the CEA envelopes…</p>}
      {query.isError && (
        <p role="alert" className="text-xs text-darkGrey">
          The CEA envelopes could not be read, so none can be chosen right now. You can still say this draws on none.
        </p>
      )}

      <div className="space-y-1">
        {options.map((e) => (
          <label
            key={e.ProjectForecastId}
            className="flex cursor-pointer items-start gap-2 rounded border border-border bg-white px-2 py-1.5 text-sm has-[:checked]:border-navy has-[:checked]:bg-lightBlue/40"
          >
            <input
              type="radio"
              name="purchase-envelope"
              className="mt-1 accent-navy"
              checked={pick === String(e.ProjectForecastId)}
              onChange={() => onPick(String(e.ProjectForecastId))}
            />
            <span>
              <span className="block font-semibold text-navy">{e.Label}</span>
              <span className="block text-xs text-darkGrey/80">{envelopeFigures(e)}</span>
              {!e.CeaApproved && (
                <span className="block text-xs text-orange">{`The CEA is ${e.CeaStatus}, not Approved yet.`}</span>
              )}
            </span>
          </label>
        ))}

        <label className="flex cursor-pointer items-start gap-2 rounded border border-border bg-white px-2 py-1.5 text-sm has-[:checked]:border-navy has-[:checked]:bg-lightBlue/40">
          <input
            type="radio"
            name="purchase-envelope"
            className="mt-1 accent-navy"
            checked={pick === 'none'}
            onChange={() => onPick('none')}
          />
          <span>This purchase draws on no CEA envelope</span>
        </label>
      </div>
    </fieldset>
  );
}

function NoticeStatus({ data }: { data: PurchaseNoticeResponse }) {
  const n = data.Notice;

  if (n?.SentAtUtc) {
    return (
      <p className="mt-2 text-sm text-darkGrey">
        Emailed to {n.RecipientCount} {n.RecipientCount === 1 ? 'person' : 'people'} on{' '}
        {formatDateTime(n.SentAtUtc)}.
        {n.Error && (
          <span className="mt-1 flex items-start gap-2 text-xs text-darkGrey/80">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-orange" aria-hidden />
            <span>Some could not be reached: {n.Error}</span>
          </span>
        )}
      </p>
    );
  }

  // ⚠ NOT SENT, AND SAID. "Nobody is on the list" must not read as "the email went".
  const why = n?.Error
    ? n.Error
    : data.RecipientsOnList === 0
      ? 'Nobody is on the purchase notice list, so no email was sent.'
      : 'It has not been sent yet.';

  return (
    <p className="mt-2 flex items-start gap-2 text-sm text-darkGrey">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange" aria-hidden />
      <span>
        <strong>Approver not emailed.</strong> {why}
      </span>
    </p>
  );
}

function Btn({
  children,
  onClick,
  primary,
}: {
  children: React.ReactNode;
  onClick: () => void;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        primary
          ? 'rounded bg-navy px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90'
          : 'rounded border border-navy bg-white px-3 py-1.5 text-sm font-semibold text-navy hover:bg-bg'
      }
    >
      {children}
    </button>
  );
}

function NoteBox({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">{label}</span>
      <textarea
        className="w-full rounded border border-border px-2 py-1.5 text-sm"
        rows={3}
        value={value}
        maxLength={MAX_NOTE}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function Problem({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="mt-3 rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
      {message}
    </p>
  );
}
